import React, { useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import AddressAutocomplete from '../../../../common/AddressAutocomplete';
import ProgramChips from '../components/ProgramChips';
import BaselineFetchProgress from '../components/BaselineFetchProgress';
import { proxyInit } from '../api';

export default function ProxyInitStep({ initial, onComplete }) {
    const [zip, setZip] = useState(initial?.zip_code || '');
    const [programs, setPrograms] = useState(initial?.programs?.length ? initial.programs : ['LIHTC']);
    const [address, setAddress] = useState(initial?.address || '');
    const [validationError, setValidationError] = useState('');

    const fetchBaseline = useMutation({
        mutationFn: async ({ zip, programs, address }) => {
            const res = await proxyInit({ zip_code: zip, programs, address });
            if (!res.ok) {
                let detail = 'Could not fetch baseline';
                try { detail = (await res.json()).detail || detail; } catch (_) {}
                throw new Error(detail);
            }
            return await res.json();
        },
        onSuccess: (data, { zip, programs, address }) => {
            onComplete({ zip_code: zip, programs, address, baseline: data });
        },
    });

    const loading = fetchBaseline.isPending;
    const error = validationError || fetchBaseline.error?.message || '';

    const submit = () => {
        if (!/^\d{5}$/.test(zip)) {
            setValidationError('Enter a valid 5-digit ZIP code');
            return;
        }
        if (programs.length === 0) {
            setValidationError('Select at least one program');
            return;
        }
        setValidationError('');
        fetchBaseline.mutate({ zip, programs, address });
    };

    return (
        <div className="max-w-xl mx-auto">
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">Start with a baseline</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                Enter the ZIP code and program type. JPRO pulls live market, oversight, and affordability proxies
                from HUD and Census so your first score isn't a blank screen.
            </p>

            <div className="space-y-4">
                <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">ZIP Code</span>
                    <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                           placeholder="10001"
                           className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500" />
                </label>

                <div>
                    <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                        Programs <span className="text-xs text-slate-500 font-normal">— select all that apply (layered subsidies)</span>
                    </span>
                    <ProgramChips value={programs} onChange={setPrograms} />
                </div>

                <label className="block">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                        Address <span className="text-xs text-slate-500 font-normal">(optional — picking one auto-fills ZIP)</span>
                    </span>
                    <div className="mt-1">
                        <AddressAutocomplete
                            value={address}
                            onChange={setAddress}
                            onPlaceSelect={(p) => {
                                setAddress(p.address);
                                if (p.zip_code) setZip(p.zip_code);
                            }}
                            placeholder="123 Main St, New York, NY"
                        />
                    </div>
                </label>

                {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}

                <button onClick={submit} disabled={loading}
                        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-md disabled:opacity-50 shadow-sm transition-colors">
                    {loading ? <Loader2 className="animate-spin" size={16} /> : null}
                    {loading ? 'Fetching baseline…' : 'Fetch Baseline'}
                    {!loading && <ArrowRight size={16} />}
                </button>

                {loading && (
                    <div className="pt-2">
                        <BaselineFetchProgress active={loading} />
                    </div>
                )}
            </div>
        </div>
    );
}
